/**
 * Export utilities for flashcard data
 */

// Export deck to JSON format
export const exportToJSON = (deckName, cards) => {
  const data = {
    name: deckName,
    cards: cards,
    exportDate: new Date().toISOString(),
    version: '1.0'
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${deckName.replace(/\s+/g, '_')}_deck.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Export deck to CSV format
export const exportToCSV = (deckName, cards) => {
  const headers = ['Arabic', 'English'];
  const rows = cards.map(card => [
    `"${card.ar.replace(/"/g, '""')}"`,
    `"${card.en.replace(/"/g, '""')}"`
  ]);

  const csv = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${deckName.replace(/\s+/g, '_')}_deck.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Export all data (backup)
export const exportBackup = (customDecks, memorized, theme, reviewData, favorites) => {
  const data = {
    customDecks,
    memorized,
    favorites: favorites || [],
    theme,
    reviewData: reviewData || {},
    exportDate: new Date().toISOString(),
    version: '1.0'
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `kalamly_backup_${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Parse imported JSON deck
export const parseJSONDeck = (jsonString) => {
  try {
    const data = JSON.parse(jsonString);

    // Validate structure
    if (!data.name || !Array.isArray(data.cards)) {
      throw new Error('Invalid deck format');
    }

    // Validate cards
    const validCards = data.cards.every(card =>
      card.ar && card.en && typeof card.ar === 'string' && typeof card.en === 'string'
    );

    if (!validCards) {
      throw new Error('Invalid card format');
    }

    return {
      name: data.name,
      cards: data.cards
    };
  } catch (error) {
    throw new Error('Failed to parse JSON: ' + error.message);
  }
};

// Parse imported CSV deck
export const parseCSVDeck = (csvString, deckName) => {
  try {
    const lines = csvString.trim().split('\n');

    // Skip header row if it exists
    const startIndex = lines[0].toLowerCase().includes('arabic') ? 1 : 0;

    const cards = lines.slice(startIndex).map((line, index) => {
      // Simple CSV parser (handles quoted fields)
      const matches = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g);

      if (!matches || matches.length < 2) {
        throw new Error(`Invalid CSV format at line ${index + 1}`);
      }

      const [ar, en] = matches.map(field =>
        field.replace(/^"(.*)"$/, '$1').replace(/""/g, '"').trim()
      );

      if (!ar || !en) {
        throw new Error(`Empty fields at line ${index + 1}`);
      }

      return { ar, en };
    });

    if (cards.length === 0) {
      throw new Error('No cards found in CSV');
    }

    return {
      name: deckName || 'Imported Deck',
      cards
    };
  } catch (error) {
    throw new Error('Failed to parse CSV: ' + error.message);
  }
};

// Parse backup file
export const parseBackup = (jsonString) => {
  try {
    const data = JSON.parse(jsonString);

    // Validate structure
    if (!data.customDecks || !Array.isArray(data.memorized)) {
      throw new Error('Invalid backup format');
    }

    return {
      customDecks: data.customDecks || {},
      memorized: data.memorized || [],
      favorites: data.favorites || [],
      theme: data.theme || 'light',
      reviewData: data.reviewData || {}
    };
  } catch (error) {
    throw new Error('Failed to parse backup: ' + error.message);
  }
};
